import { POApprovalStatus } from "types/enum";

import { add, divide, multiply, subtract } from "utilities/math/math";

import { PercentageSection } from "commonComponents/utilities/PercentageDisplayBar/PercentageDisplayBar.types";

import { type PaymentStatus } from "entity/purchaseOrder/PurchaseOrder/PurchaseOrder.api.types";
import { type POPaymentStatus } from "entity/purchaseOrderPayment/common/PurchaseOrderPaymentStatus/PurchaseOrderPaymentStatus";

export function getApprovalSignatureRequired(assignedStatus: any) {
    if (assignedStatus && assignedStatus.actions.length > 0) {
        const approvalAction = assignedStatus.actions.filter(
            (action: any) => action.id === POApprovalStatus.ApprovedBySub,
        );
        return approvalAction && approvalAction.length === 1 && approvalAction[0].signatureRequired;
    }
    return false;
}

export function getPoName(poName: any) {
    return `${poName.title}${poName.value}`;
}

export function getTitle(title: any) {
    return title.value;
}

export const getPercentageFromSection = (
    section: PercentageSection,
    totalAmount: number,
    canViewPrice: boolean,
) => {
    if (!canViewPrice) {
        return section.percentage;
    }
    const percentage =
        totalAmount === 0 ? 0 : multiply(divide(section.value ?? 0, totalAmount), 100).toNumber();
    return percentage;
};

export const getOutstandingSection = (paymentStatus: PaymentStatus, canViewPrice: boolean) => {
    let outstandingValue: number | undefined;
    let outstandingPercentage: number | undefined;
    if (canViewPrice) {
        const billedValue = paymentStatus.percentageDisplay.reduce(
            (acc, currentVal) => add(acc, currentVal.value!).toNumber(),
            0,
        );
        outstandingValue = subtract(paymentStatus.totalAmount, billedValue).toNumber();
        outstandingPercentage =
            paymentStatus.totalAmount === 0
                ? 0
                : divide(outstandingValue, paymentStatus.totalAmount).toNumber();
    } else {
        const billedPercentage = paymentStatus.percentageDisplay.reduce(
            (acc, currentVal) => add(acc, currentVal.percentage).toNumber(),
            0,
        );
        outstandingPercentage = subtract(100, billedPercentage).toNumber();
    }
    const outstandingSection = new PercentageSection({
        color: "",
        label: "Outstanding",
        isOutstanding: true,
        value: outstandingValue,
        percentage: outstandingPercentage,
        striped: false,
    });
    return outstandingSection;
};

export class SubPaymentListResponse {
    constructor(data: any) {
        this.purchaseOrderId = data.purchaseOrderId;
        this.price = data.price;
        this.amountRemaining = data.amountRemaining;
        this.totalPaymentAmount = data.totalPaymentAmount;
        this.canRequestNew = data.canRequestNew;
        this.savedPayments = data.savedPayments.map((payment: any) => new SavedPayment(payment));
    }

    purchaseOrderId: number;
    price: string;
    amountRemaining: string;
    totalPaymentAmount: string;
    canRequestNew: boolean;
    savedPayments: SavedPayment[];
}

export class SavedPayment {
    constructor(data: any) {
        this.purchaseOrderPaymentId = data.purchaseOrderPaymentId;
        this.title = data.title;
        this.status = data.status;
        this.requestedStatusText = data.requestedStatusText;
        this.additionalStatusText = data.additionalStatusText;
        this.paymentAmount = data.paymentAmount;
        this.paymentNotes = data.paymentNotes;
        this.canRequest = data.canRequest;
    }

    purchaseOrderPaymentId: number;
    title: string;
    status: POPaymentStatus;
    requestedStatusText: string;
    additionalStatusText: string;
    paymentAmount: string;
    paymentNotes: string;
    canRequest: boolean;
}
